import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { addCharacter, addFamily, selectFamilies } from './charactersSlice';
import useThemeStyles from '../../hooks/useThemeStyles';
import characterService from '../../utils/apiRequests/characterService';

const ROLE_OPTIONS = ['Protagonist', 'Antagonist', 'Supporting', 'Minor'];

const AddCharacterModal = ({ open, onClose, onCharacterAdded, projectId, familyId }) => {
  const dispatch = useDispatch();
  const { colors } = useThemeStyles();
  const allFamilies = useSelector(selectFamilies);
  const families = useMemo(
    () => allFamilies.filter((f) => f.projectId === projectId),
    [allFamilies, projectId]
  );

  const [formData, setFormData] = useState({
    fullName: '',
    familyId: familyId || null,
    role: 'Protagonist',
    notes: '',
    // More details fields
    age: '',
    race: '',
    hairColor: '',
    eyeColor: '',
    sexuality: '',
    personality: '',
    tags: [],
  });

  const [newFamilyName, setNewFamilyName] = useState('');
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [openProfileAfterCreate, setOpenProfileAfterCreate] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const sexualityOptions = [
    { value: '', label: 'Not specified' },
    { value: 'Heterosexual', label: 'Heterosexual' },
    { value: 'Homosexual', label: 'Homosexual' },
    { value: 'Bisexual', label: 'Bisexual' },
    { value: 'Pansexual', label: 'Pansexual' },
    { value: 'Asexual', label: 'Asexual' },
    { value: 'Demisexual', label: 'Demisexual' },
    { value: 'Queer', label: 'Queer' },
    { value: 'Other', label: 'Other' },
  ];

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleRoleChange = (role) => {
    setFormData((prev) => ({
      ...prev,
      role,
    }));
  };

  const handleFamilyChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      // User typed a new family name
      setNewFamilyName(newValue);
      setFormData((prev) => ({
        ...prev,
        familyId: null,
      }));
    } else if (newValue && newValue.id) {
      // User selected an existing family
      setFormData((prev) => ({
        ...prev,
        familyId: newValue.id,
      }));
      setNewFamilyName('');
    } else {
      // Cleared selection
      setFormData((prev) => ({
        ...prev,
        familyId: null,
      }));
      setNewFamilyName('');
    }
    // Clear error when family changes
    if (errors.family) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.family;
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const newErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.role) {
      newErrors.role = 'Role in story is required';
    }
    if (!formData.familyId && !newFamilyName.trim()) {
      newErrors.family = 'Family / Last Name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      // Handle family creation if new family name was provided
      let finalFamilyId = formData.familyId;
      if (newFamilyName.trim() && !formData.familyId) {
        // Create new family via API
        const familyResponse = await characterService.createFamily(projectId, {
          familyName: newFamilyName.trim(),
          members: [],
          connections: [],
        });

        if (familyResponse.error) {
          setErrors({ family: 'Failed to create family. Please try again.' });
          setSubmitting(false);
          return;
        }

        // Normalize and add family to Redux
        const normalizedFamily = {
          ...familyResponse.data.data,
          id:
            familyResponse.data.data.id ||
            familyResponse.data.data._id?.toString() ||
            familyResponse.data.data._id,
          projectId:
            familyResponse.data.data.projectId?.toString() || familyResponse.data.data.projectId,
          members: (familyResponse.data.data.members || []).map((m) =>
            typeof m === 'object' && m._id ? m._id.toString() : m?.toString() || m
          ),
          createdAt: familyResponse.data.data.createdAt
            ? new Date(familyResponse.data.data.createdAt).getTime()
            : Date.now(),
          updatedAt: familyResponse.data.data.updatedAt
            ? new Date(familyResponse.data.data.updatedAt).getTime()
            : Date.now(),
        };
        dispatch(addFamily(normalizedFamily));
        finalFamilyId = normalizedFamily.id;
      }

      // Add role to tags if not already present
      const tags = [...(formData.tags || [])];
      if (!tags.includes(formData.role)) {
        tags.push(formData.role);
      }

      // Create character via API
      const characterData = {
        fullName: formData.fullName.trim(),
        displayName: formData.fullName.trim(),
        role: formData.role,
        notes: formData.notes || '',
        age: formData.age ? parseInt(formData.age, 10) : null,
        race: formData.race || '',
        hairColor: formData.hairColor || '',
        eyeColor: formData.eyeColor || '',
        sexuality: formData.sexuality || '',
        personality: formData.personality || '',
        tags,
        familyId: finalFamilyId || null,
      };

      const characterResponse = await characterService.createCharacter(projectId, characterData);

      if (characterResponse.error) {
        setErrors({ submit: 'Failed to create character. Please try again.' });
        setSubmitting(false);
        return;
      }

      // Normalize and add character to Redux
      const normalizedCharacter = {
        ...characterResponse.data.data,
        id:
          characterResponse.data.data.id ||
          characterResponse.data.data._id?.toString() ||
          characterResponse.data.data._id,
        projectId:
          characterResponse.data.data.projectId?.toString() ||
          characterResponse.data.data.projectId,
        familyId:
          characterResponse.data.data.familyId?.toString() ||
          characterResponse.data.data.familyId ||
          null,
        createdAt: characterResponse.data.data.createdAt
          ? new Date(characterResponse.data.data.createdAt).getTime()
          : Date.now(),
        updatedAt: characterResponse.data.data.updatedAt
          ? new Date(characterResponse.data.data.updatedAt).getTime()
          : Date.now(),
      };
      dispatch(addCharacter(normalizedCharacter));

      // Reset form
      setFormData({
        fullName: '',
        familyId: familyId || null,
        role: 'Protagonist',
        notes: '',
        age: '',
        race: '',
        hairColor: '',
        eyeColor: '',
        sexuality: '',
        personality: '',
        tags: [],
      });
      setNewFamilyName('');
      setShowMoreDetails(false);
      setOpenProfileAfterCreate(false);

      // Call onCharacterAdded callback if provided
      if (onCharacterAdded) {
        onCharacterAdded(normalizedCharacter.id, openProfileAfterCreate);
      }

      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error creating character:', err);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      fullName: '',
      familyId: familyId || null,
      role: 'Protagonist',
      notes: '',
      age: '',
      race: '',
      hairColor: '',
      eyeColor: '',
      sexuality: '',
      personality: '',
      tags: [],
    });
    setNewFamilyName('');
    setShowMoreDetails(false);
    setOpenProfileAfterCreate(false);
    setErrors({});
    if (onClose) {
      onClose();
    }
  };

  // Prepare family options for Autocomplete
  const familyOptions = families.map((family) => ({
    id: family.id,
    label: family.familyName,
  }));

  // Get selected family for Autocomplete
  const selectedFamily = formData.familyId
    ? familyOptions.find((opt) => opt.id === formData.familyId)
    : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.paper.background,
          color: colors.text.primary,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: colors.text.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Add Character
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary, fontWeight: 400 }}>
            Start simple — you can fill in details later.
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.text.primary,
              backgroundColor: colors.background.surface,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Full Name */}
          <TextField
            label="Full Name *"
            value={formData.fullName}
            onChange={handleChange('fullName')}
            fullWidth
            size="small"
            required
            error={!!errors.fullName}
            helperText={errors.fullName}
            placeholder="e.g. John Smith"
            sx={{
              '& .MuiInputLabel-root': {
                color: colors.text.secondary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.interactive.primary,
              },
            }}
          />

          {/* Family / Last Name */}
          <Autocomplete
            options={familyOptions}
            value={selectedFamily}
            inputValue={newFamilyName}
            onInputChange={(event, newInputValue) => {
              setNewFamilyName(newInputValue);
              // Clear error when user starts typing
              if (errors.family) {
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.family;
                  return newErrors;
                });
              }
            }}
            onChange={handleFamilyChange}
            freeSolo
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label="Family / Last Name *"
                size="small"
                required
                error={!!errors.family}
                helperText={errors.family}
                placeholder="Select or create family..."
                sx={{
                  '& .MuiInputLabel-root': {
                    color: colors.text.secondary,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.interactive.primary,
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <Box component="li" {...props}>
                {option.label}
              </Box>
            )}
          />

          {/* Role in Story */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: colors.text.secondary,
                fontWeight: 500,
              }}
            >
              Role in Story *
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {ROLE_OPTIONS.map((role) => (
                <Button
                  key={role}
                  variant={formData.role === role ? 'contained' : 'outlined'}
                  onClick={() => handleRoleChange(role)}
                  sx={{
                    flex: 1,
                    minWidth: '120px',
                    backgroundColor:
                      formData.role === role ? colors.interactive.primary : 'transparent',
                    color: formData.role === role ? colors.paper.background : colors.text.primary,
                    borderColor:
                      formData.role === role ? colors.interactive.primary : colors.border.subtle,
                    '&:hover': {
                      backgroundColor:
                        formData.role === role
                          ? colors.interactive.hover
                          : colors.background.surface,
                      borderColor: colors.interactive.primary,
                    },
                    textTransform: 'none',
                    fontWeight: formData.role === role ? 600 : 400,
                  }}
                >
                  {role}
                </Button>
              ))}
            </Stack>
            {errors.role && (
              <Typography
                variant="caption"
                sx={{ color: colors.semantic.error, mt: 0.5, display: 'block' }}
              >
                {errors.role}
              </Typography>
            )}
          </Box>

          {/* More Details Collapsible */}
          <Box>
            <Button
              onClick={() => setShowMoreDetails(!showMoreDetails)}
              startIcon={showMoreDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                color: colors.text.secondary,
                textTransform: 'none',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: colors.text.primary,
                },
              }}
            >
              More Details
            </Button>
            <Collapse in={showMoreDetails}>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="Age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange('age')}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Race / Ethnicity"
                  value={formData.race}
                  onChange={handleChange('race')}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Hair Color"
                  value={formData.hairColor}
                  onChange={handleChange('hairColor')}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Eye Color"
                  value={formData.eyeColor}
                  onChange={handleChange('eyeColor')}
                  fullWidth
                  size="small"
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Sexuality</InputLabel>
                  <Select
                    value={formData.sexuality}
                    onChange={handleChange('sexuality')}
                    label="Sexuality"
                  >
                    {sexualityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Personality"
                  value={formData.personality}
                  onChange={handleChange('personality')}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Short summary of personality traits..."
                />
              </Stack>
            </Collapse>
          </Box>

          {/* Character Notes */}
          <TextField
            label="Character Notes"
            value={formData.notes}
            onChange={handleChange('notes')}
            fullWidth
            multiline
            rows={4}
            size="small"
            placeholder="Voice, backstory, flaws, secrets, first impressions..."
            sx={{
              '& .MuiInputLabel-root': {
                color: colors.text.secondary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.interactive.primary,
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 2,
          borderTop: `1px solid ${colors.border.subtle}`,
          justifyContent: 'space-between',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={openProfileAfterCreate}
              onChange={(e) => setOpenProfileAfterCreate(e.target.checked)}
              sx={{
                color: colors.interactive.primary,
                '&.Mui-checked': {
                  color: colors.interactive.primary,
                },
              }}
            />
          }
          label="Open character profile after creating"
          sx={{
            color: colors.text.secondary,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button onClick={handleClose} sx={{ color: colors.text.secondary }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: colors.interactive.primary,
              color: colors.paper.background,
              '&:hover': {
                backgroundColor: colors.interactive.hover,
              },
            }}
          >
            {submitting ? 'Creating...' : 'Create Character'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

AddCharacterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCharacterAdded: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  familyId: PropTypes.string,
};

AddCharacterModal.defaultProps = {
  onCharacterAdded: null,
  familyId: null,
};

export default AddCharacterModal;
